// Shared pool of groupProcessor workers — replaces the per-instance worker created
// by each useWebSocket call (~22 on a typical trader's setup).
//
// Pool sizing: floor(hardwareConcurrency / 2), min 2. On an 8-core machine this
// gives 4 workers — enough to process 4 views in parallel while keeping 4 threads
// free for the main thread, browser internals, and OS. Adapts automatically at
// runtime so traders on 16-core workstations get 8 workers.
//
// Coalescing: each consumer has at most one pending job slot. If a consumer
// submits jobs faster than a worker can process them, only the latest payload is
// kept — older intermediate states are discarded. The UI always renders the most
// recent data without wasting CPU on stale snapshots.

const POOL_SIZE = Math.max(
  2,
  Math.floor((navigator.hardwareConcurrency ?? 8) / 2),
);

interface WorkerSlot {
  worker: Worker;
  busy: boolean;
}

class GroupWorkerPool {
  private readonly _slots: WorkerSlot[];
  private readonly _pending = new Map<string, object>(); // one slot per consumer
  private readonly _callbacks = new Map<string, (result: unknown) => void>();
  private readonly _latestId = new Map<string, number>(); // stale-result guard per consumer
  private _nextMsgId = 0;

  constructor() {
    this._slots = Array.from({ length: POOL_SIZE }, () => {
      const slot: WorkerSlot = {
        worker: new Worker(
          new URL("../workers/groupProcessor.worker.ts", import.meta.url),
          { type: "module" },
        ),
        busy: false,
      };
      slot.worker.onmessage = (e: MessageEvent) => this._onMessage(slot, e);
      slot.worker.onerror = (e: ErrorEvent) =>
        console.error("groupProcessor worker error:", e.message);
      return slot;
    });
  }

  private _onMessage(slot: WorkerSlot, e: MessageEvent): void {
    slot.busy = false;
    const { consumerId, id, result, error } = e.data;
    if (id === this._latestId.get(consumerId) && !error) {
      const cb = this._callbacks.get(consumerId);
      if (cb) cb(result);
    }
    this._drainPending(slot);
  }

  private _drainPending(slot: WorkerSlot): void {
    if (this._pending.size === 0) return;
    const next = this._pending.entries().next();
    if (next.done) return;
    const [consumerId, payload] = next.value;
    this._pending.delete(consumerId);
    this._dispatch(slot, consumerId, payload);
  }

  private _dispatch(slot: WorkerSlot, consumerId: string, payload: object): void {
    slot.busy = true;
    const id = ++this._nextMsgId;
    this._latestId.set(consumerId, id);
    slot.worker.postMessage({ id, consumerId, ...(payload as Record<string, unknown>) });
  }

  register(consumerId: string, cb: (result: unknown) => void): void {
    this._callbacks.set(consumerId, cb);
  }

  unregister(consumerId: string): void {
    this._callbacks.delete(consumerId);
    this._pending.delete(consumerId);
    this._latestId.delete(consumerId);
  }

  submit(consumerId: string, payload: object): void {
    const free = this._slots.find((s) => !s.busy);
    if (free) {
      this._dispatch(free, consumerId, payload);
    } else {
      // Coalesce: overwrite any previous pending payload — only the latest state matters
      this._pending.set(consumerId, payload);
    }
  }
}

export const groupWorkerPool = new GroupWorkerPool();
